import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import {
  dismissNotification,
  listNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from "@/lib/api/notifications";
import { clearAuth } from "@/data/slices/authSlice";

const sortNotifications = (items) =>
  [...items].sort((left, right) => {
    const leftTime = Date.parse(left.createdAtUtc || left.createdAt || 0);
    const rightTime = Date.parse(right.createdAtUtc || right.createdAt || 0);
    return rightTime - leftTime;
  });

export const fetchNotifications = createAsyncThunk(
  "notifications/fetch",
  async ({ limit = 50, before } = {}) => {
    return listNotifications({ limit, before });
  },
);

export const markNotificationAsRead = createAsyncThunk(
  "notifications/markRead",
  async (id) => {
    return { id, response: await markNotificationRead(id) };
  },
);

export const markAllNotificationsAsRead = createAsyncThunk(
  "notifications/markAllRead",
  async () => markAllNotificationsRead(),
);

export const dismissNotificationById = createAsyncThunk(
  "notifications/dismiss",
  async (id) => {
    return { id, response: await dismissNotification(id) };
  },
);

const initialState = {
  items: [],
  unreadCount: 0,
  loading: false,
  error: null,
  lastFetchedAt: null,
};

const upsertNotification = (state, notification) => {
  if (!notification?.id) return;

  const index = state.items.findIndex((item) => item.id === notification.id);
  if (index >= 0) {
    state.items[index] = {
      ...state.items[index],
      ...notification,
    };
  } else {
    state.items.unshift(notification);
  }

  state.items = sortNotifications(state.items);
};

const notificationsSlice = createSlice({
  name: "notifications",
  initialState,
  reducers: {
    notificationReceived(state, action) {
      const existing = state.items.find((item) => item.id === action.payload?.id);
      upsertNotification(state, action.payload);
      if (!existing && !action.payload?.readAtUtc) {
        state.unreadCount += 1;
      }
    },
    notificationsSummaryChanged(state, action) {
      state.unreadCount = action.payload?.unreadCount ?? state.unreadCount;
    },
    clearNotifications() {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchNotifications.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchNotifications.fulfilled, (state, action) => {
        state.loading = false;
        state.items = sortNotifications(action.payload?.items ?? []);
        state.unreadCount = action.payload?.unreadCount ?? 0;
        state.lastFetchedAt = new Date().toISOString();
      })
      .addCase(fetchNotifications.rejected, (state) => {
        state.loading = false;
        state.error = "components.notifications.errors.load_failed";
      })
      .addCase(markNotificationAsRead.fulfilled, (state, action) => {
        const item = state.items.find((candidate) => candidate.id === action.payload.id);
        if (item) item.readAtUtc = item.readAtUtc || new Date().toISOString();
        state.unreadCount =
          action.payload.response?.summary?.unreadCount ?? state.unreadCount;
      })
      .addCase(markAllNotificationsAsRead.fulfilled, (state, action) => {
        const now = new Date().toISOString();
        state.items.forEach((item) => {
          item.readAtUtc = item.readAtUtc || now;
        });
        state.unreadCount =
          action.payload?.summary?.unreadCount ?? 0;
      })
      .addCase(dismissNotificationById.fulfilled, (state, action) => {
        state.items = state.items.filter((item) => item.id !== action.payload.id);
        state.unreadCount =
          action.payload.response?.summary?.unreadCount ?? state.unreadCount;
      })
      .addCase(clearAuth, () => initialState);
  },
});

export const {
  notificationReceived,
  notificationsSummaryChanged,
  clearNotifications,
} = notificationsSlice.actions;

export default notificationsSlice.reducer;
